import { Grid2, Typography, Link, Divider, IconButton } from "@mui/material";
import { legalTermsSchema } from "../enum/formEnum";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";
import { parseBoolean } from "../../Helper/utils";
import moment from "moment";
import { replaceDescriptionPlaceholders, renderMarkdownBold } from "../enum/descriptionUtils";

const filePath = process.env.REACT_APP_S3BUCKET_FILEURL;


const getSubmittedDate = (createdAt) =>
  createdAt
    ? moment
        .utc(createdAt)
        .tz(dataConstant.centralAmericaTimeZone)
        .format(dataConstant.dateFormatUSFormat)
    : moment().tz(dataConstant.centralAmericaTimeZone).format(dataConstant.dateFormatUSFormat);

const LegalTermsDetail = ({ formData, fromReview }) => {
  const windowPath = window.location.pathname;
  const getUserdetail = useSelector(selectUserDetail);
  const roles = getUserdetail?.roles;
  const isInsuranceDisable = !(
    (roles === null || roles === undefined) &&
    windowPath.toLowerCase() === "/clientintake"
  );

  const getInsuranceAgreementText = (value, agreementText) => {
    const agreedValue = typeof value === "object" ? value?.agreed : value;

    if (!parseBoolean(agreedValue)) {
      return "Not Applicable";
    }

    if (typeof value !== "object") {
      return agreementText;
    }

    const selectedOption =
      dataConstant.insurancePolicyOptions[value.policyDeliveryOption];

    const documentNames = value.policyDocuments?.length
      ? value.policyDocuments.map((document) => document.name).filter(Boolean)
      : [value.policyDocument?.name].filter(Boolean);

    const sections = [agreementText, "", "Policy Delivery:", selectedOption];

    if (documentNames.length > 0) {
      sections.push(
        "",
        "Policy Documents:",
        ...documentNames.map((name) => `• ${name}`),
      );
    }

    return sections.join("\n");
  };

  const handleDocumentView = (event, policyDocument) => {
    if (!policyDocument) return;

    event.preventDefault();
    event.stopPropagation();

    // Existing document stored in S3
    if (policyDocument.isExisting && policyDocument.filePath && filePath) {
      const policyPath = `${filePath.replace(/\/$/, "")}/${policyDocument.filePath.replace(/^\//, "")}`;
      window.open(policyPath, "_blank", "noopener,noreferrer");
      return;
    }

    // Newly uploaded document (Base64)
    if (policyDocument.content) {
      window.open(policyDocument.content, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <Typography variant="h4" color="default.main">
        Legal Terms & Agreements
      </Typography>

      <Grid2 container spacing={3} mt={3}>
        {legalTermsSchema.map((section) => {
          let value = formData?.[section.key]?.[section.fieldKey];

          if (
            !value &&
            section?.key === "overtimeAgreement" &&
            Boolean(formData?.paymentDetailsSection0?.overtimeAgreement) ===
              true
          ) {
            value = Boolean(
              formData?.paymentDetailsSection0?.overtimeAgreement,
            );
          }

          const aggreementText =
            section?.key === "obligationOfResponsibleParty"
              ? "Responsible Party Obligation"
              : section.title;
          return (
            <Grid2 size={{ xs: 12, sm: 12 }} key={section.key}>
              {/* Section Title */}
              <Typography variant="h6" color="default.main" gutterBottom>
                {section.title}
              </Typography>

              {/* Description */}
              {section.key !== "arbitration" &&
                section.key !== "hospitalization" &&
                section.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    paragraph
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {renderMarkdownBold(
                      replaceDescriptionPlaceholders(
                        section.description,
                        formData,
                      ),
                    )}
                  </Typography>
                )}

              {section.key === "clientServicePlanAcknowledgement" && (
                <Grid2 item xs={12} sm={12}>
                  {(() => {
                    const signatureLabel =
                      "Signature of Client or Client Contact";
                    const printedNameLabel =
                      "Printed Name of Client/Client Contact";
                    const clientSignature =
                      value?.clientSignatureAndContactInfo?.signatureOfClient;
                    const agencyRepresentativeLabel =
                      "Ernie Cote\nChief Operating Officer\nAgency Representative";

                    return (
                      <>
                        <Grid2 container spacing={10}>
                          {/* Signature of Client or Client Contact */}
                          <Grid2 item xs={12} sm={6} mb={2}>
                            <Typography variant="body2" color="default.main" mb={0.5}>
                              {signatureLabel}
                            </Typography>

                            {clientSignature ? (
                              <img
                                src={clientSignature}
                                alt={signatureLabel}
                                style={{ maxWidth: "100%", maxHeight: "200px" }}
                              />
                            ) : (
                              <Typography variant="body1" color="default.light">
                                No signature provided
                              </Typography>
                            )}
                          </Grid2>

                          {/* Date */}
                          <Grid2 item xs={12} sm={6} mb={2}>
                            <Typography variant="body2" color="default.main" mb={0.5}>
                              Date
                            </Typography>
                            <Typography variant="body1" color="default.light">
                              {value?.date
                                ? new Date(value.date).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })
                                : "-"}
                            </Typography>
                          </Grid2>
                        </Grid2>

                        {/* Printed Name of Client/Client Contact */}
                        <Grid2 item xs={12} sm={12} mb={2}>
                          <Typography variant="body2" color="default.main" mb={0.5}>
                            {printedNameLabel}
                          </Typography>
                          <Typography variant="body1" color="default.light">
                            {value?.printedClientName || "-"}
                          </Typography>
                        </Grid2>

                        {/* Agency Representative */}
                        <Grid2 container xs={12} sm={6}>
                          <Grid2 item xs={6} sm={6} mb={2}>
                            <img
                              src={urlConstant.SignImage.ErnieSignImageUrl}
                              alt="Agency Representative"
                              style={{ maxWidth: "80%", maxHeight: "200px" }}
                            />
                            <Divider sx={{ borderBottomWidth: 2, maxWidth: "80%" }} />
                            <Typography
                              variant="body2"
                              color="default.main"
                              mb={0.5}
                              sx={{ whiteSpace: "pre-line" }}
                            >
                              {agencyRepresentativeLabel}
                            </Typography>
                          </Grid2>
                        </Grid2>
                      </>
                    );
                  })()}
                </Grid2>
              )}

              {/* Special case: Arbitration & Jury Trial Waiver extra UI */}
              {section.key === "arbitration" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ whiteSpace: "pre-line" }}
                >
                  All disputes regarding any aspect of this Engagement
                  Agreement, including, without limitation, enforcement or
                  interpretation of this Agreement, recovery of any amount due
                  hereunder, or any claim for injury, harm or damage in any way
                  associated with any goods or services provided hereunder,
                  shall be subject to determination by binding arbitration under
                  the rules of the American Arbitration Association and any such
                  arbitration shall be conducted in Dallas County, Texas, in
                  accordance with Texas law, except for its conflicts of laws
                  principles.
                  <b>
                    THE PARTIES HEREBY WAIVE AND AGREE NOT TO INVOKE ANY
                    STATUTORY, CONSTITUTIONAL OR COMMON LAW RIGHT TO A TRIAL BY
                    JURY REGARDLESS OF WHETHER SUCH RIGHT ARISES UNDER STATE OR
                    FEDERAL LAW.
                  </b>
                </Typography>
              )}

              {section.key === "hospitalization" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ whiteSpace: "pre-line" }}
                >
                  <>
                    <i>
                      This policy is designed to balance the needs of Clients
                      during unplanned medical events with the Cambridge
                      Caregiver's obligation to maintain predictable scheduling
                      and income stability for its Caregivers.
                    </i>
                    <br />
                    <br />
                    If the Client or the Client's Authorized Representative
                    requests continued caregiving services during a
                    hospitalization, the assigned Caregiver may be redirected to
                    the hospital to provide services for the duration of the
                    scheduled shift.
                  </>
                </Typography>
              )}

              {/* Special case: Privacy Consent extra UI */}
              {section.key === "privacyConsent" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ whiteSpace: "pre-line" }}
                >
                  I agree with the storage and handling of my data by this
                  website found at{" "}
                  <Link
                    href="https://cambridgecaregivers.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://cambridgecaregivers.com/privacy-policy/
                  </Link>
                  .
                </Typography>
              )}

              {section.key === "insurance" && (
                <>
                  {isInsuranceDisable && !fromReview ? (
                    <>
                      {/* Agreement */}
                      <Typography
                        variant="body1"
                        color="secondary.dark"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {value === false ||
                        value?.agreed === "false" ||
                        value?.agreed === false ||
                        !value
                          ? "Not Applicable"
                          : section.agreementText}
                      </Typography>

                      {/* Policy Delivery */}
                      {(value?.agreed === "true" || value?.agreed === true) &&
                        value?.policyDeliveryOption && (
                          <>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              mt={2}
                            >
                              Policy Delivery:
                            </Typography>

                            <Typography variant="body2" color="secondary.dark">
                              {
                                dataConstant.insurancePolicyOptions[
                                  value.policyDeliveryOption
                                ]
                              }
                            </Typography>
                          </>
                        )}

                      {/* Policy Documents */}
                      {(value?.agreed === "true" || value?.agreed === true) &&
                        value?.policyDocuments?.length > 0 && (
                          <>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              mt={2}
                            >
                              Policy Documents:
                            </Typography>

                            {value.policyDocuments.map((document, index) => (
                              <Grid2
                                key={index}
                                container
                                alignItems="center"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="body2"
                                  color="secondary.dark"
                                >
                                  • {document.name}
                                </Typography>

                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label={`View ${document.name}`}
                                  onClick={(event) =>
                                    handleDocumentView(event, document)
                                  }
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Grid2>
                            ))}
                          </>
                        )}
                    </>
                  ) : (
                    <Typography
                      variant="body1"
                      color="secondary.dark"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {getInsuranceAgreementText(value, section.agreementText)}
                    </Typography>
                  )}
                </>
              )}

              {/* Agreement Result */}
              {section.key !== "clientServicePlanAcknowledgement" &&
                section.key !== "insurance" && (
                  <Typography variant="body1" color="secondary.dark">
                    {value === true
                      ? section.agreementText
                      : `No, I do not agree to the ${aggreementText} terms`}
                  </Typography>
                )}
            </Grid2>
          );
        })}
      </Grid2>
    </>
  );
};

export default LegalTermsDetail;
